import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { documentsApi } from '@/lib/documents';
import { agentsApi } from '@/lib/agents';
import { AppShell } from '@/components/app/AppShell';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { modelTierLabel } from '@/lib/model-label';
import { SaveResultButton } from '@/components/app/SaveResultButton';
import { ResultFooter } from '@/components/app/ResultFooter';
import { OutputLanguageSelect } from '@/components/app/OutputLanguageSelect';
import { HelpHint } from '@/components/app/HelpHint';
import { LanguageCode, defaultOutputLanguage } from '@/i18n/languages';

export function CompareAgentPage() {
  const { t, i18n } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  // Radix Select rejects empty-string Item values; sentinel for "not picked yet".
  const [docAId, setDocAId] = useState<string>('__none__');
  const [docBId, setDocBId] = useState<string>('__none__');
  const [language, setLanguage] = useState<LanguageCode>(defaultOutputLanguage(i18n.language));

  const { data: docs } = useQuery({
    queryKey: ['documents', wsId, 'for-compare'],
    queryFn: () => documentsApi.list(wsId!),
    enabled: !!wsId,
  });

  const docASelected = docAId !== '__none__';
  const docBSelected = docBId !== '__none__';

  const compare = useMutation({
    mutationFn: () => agentsApi.compare(wsId!, {
      docAId: docASelected ? docAId : undefined,
      docBId: docBSelected ? docBId : undefined,
      language,
    }),
  });

  const indexedDocs = (docs ?? []).filter((d) => d.status === 'indexed');
  const docItems: DropdownItem<string>[] = [
    { value: '__none__', label: '—' },
    ...indexedDocs.map((d) => ({ value: d.id, label: d.title })),
  ];

  return (
    <AppShell
      title={t('compare_agent.title', { defaultValue: 'Compare' })}
      description={t('compare_agent.subtitle', { defaultValue: 'Side-by-side semantic comparison across two indexed documents.' }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-5">
        <HelpHint page="compare" />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('compare_agent.doc_a', { defaultValue: 'Document A' })}</label>
            <div className="w-full">
              <Dropdown
                value={docAId}
                onChange={(next) => setDocAId(next)}
                items={docItems}
                ariaLabel={t('compare_agent.doc_a', { defaultValue: 'Document A' }) as string}
                size="md"
                className="w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('compare_agent.doc_b', { defaultValue: 'Document B' })}</label>
            <div className="w-full">
              <Dropdown
                value={docBId}
                onChange={(next) => setDocBId(next)}
                items={docItems}
                ariaLabel={t('compare_agent.doc_b', { defaultValue: 'Document B' }) as string}
                size="md"
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-ink-900">{t('compose.language', { defaultValue: 'Output language' })}:</span>
          <OutputLanguageSelect value={language} onChange={setLanguage} size="sm" className="" />
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => compare.mutate()}
            disabled={!docASelected || !docBSelected || docAId === docBId || compare.isPending}
            className="btn-press px-4 py-2 text-sm disabled:opacity-40"
          >
            {compare.isPending ? t('compare_agent.comparing', { defaultValue: 'Comparing…' }) : t('compare_agent.submit', { defaultValue: 'Compare' })}
          </button>
        </div>

        {compare.data && (
          <>
            <section className="card-paper p-5">
              <h3 className="text-sm font-semibold text-ink-900 mb-2">{t('compare_agent.summary', { defaultValue: 'Summary' })}</h3>
              <CitedMarkdown content={compare.data.summary} wsId={wsId} />
              <div className="mt-2 text-xs text-ink-500">
                {modelTierLabel(compare.data.model)} · {compare.data.inputTokens + compare.data.outputTokens} tok · ¢{compare.data.costCents}
              </div>
              <ResultFooter
                wsId={wsId!}
                citedChunkIds={compare.data.citedChunkIds}
                askSeed={t('compare_agent.followup_seed', { defaultValue: 'Explain the key differences between these documents' }) as string}
              >
                <SaveResultButton
                  workspaceId={wsId!}
                  kind="compare"
                  title={t('compare_agent.title', { defaultValue: 'Compare' }) as string}
                  output={compare.data.summary}
                  citedChunkIds={compare.data.citedChunkIds}
                  model={compare.data.model}
                  costCents={compare.data.costCents}
                />
              </ResultFooter>
            </section>

            {compare.data.deltas.length > 0 && (
              <section className="card-paper p-5">
                <h3 className="text-sm font-semibold text-ink-900 mb-2">{t('compare_agent.deltas', { defaultValue: 'Differences' })}</h3>
                <table className="w-full text-xs border-collapse">
                  <thead className="text-ink-500">
                    <tr>
                      <th className="text-left py-2 pr-2 font-medium">{t('compare_agent.th_section', { defaultValue: 'Section' })}</th>
                      <th className="text-left py-2 pr-2 font-medium">{t('compare_agent.th_kind', { defaultValue: 'Kind' })}</th>
                      <th className="text-left py-2 pr-2 font-medium">{t('compare_agent.th_a', { defaultValue: 'A' })}</th>
                      <th className="text-left py-2 pr-2 font-medium">{t('compare_agent.th_b', { defaultValue: 'B' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compare.data.deltas.map((d, i) => (
                      <tr key={i} className="border-t border-ink-100 text-ink-700">
                        <td className="py-2 pr-2">{d.section}</td>
                        <td className="py-2 pr-2">{d.kind}</td>
                        <td className="py-2 pr-2">{d.claim_a ?? '—'}</td>
                        <td className="py-2 pr-2">{d.claim_b ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
